import { useEffect, useRef, type CSSProperties } from 'react';
import { reducedMotion } from '~/deck2/case/motion';
import { reel, type ReelCase } from './config';
import styles from './reel.module.css';

/** A thumbnail at full size, in the middle of the row. Matches `.slot` in reel.module.css. */
const CARD_W = 625;
const GAP = 25;

/**
 * How big a card is, by how many cards it is from the middle: one smooth bell, so a card grows and
 * shrinks at an even pace as it passes, with no kinks. One card out it is about 73% of the middle one.
 */
const SMALLEST = 0.46;
const SPREAD = 0.845;
const sizeAt = (distance: number) => SMALLEST + (1 - SMALLEST) * Math.exp(-(distance * distance) / (2 * SPREAD * SPREAD));

/** Fewer cards than this and the row would show the same one at both ends, so the list repeats. */
const MIN_CARDS = 12;

/** Cards per second, moving right, always: slow enough to read a title as it passes the middle. */
const SPEED = 1 / 6;

/** How quickly the row stops under the pointer and gets going again, in seconds (time constants). */
const STOP_IN = 0.07;
const GO_IN = 0.18;

const CARDS: { item: ReelCase; copy: number }[] = Array.from({ length: Math.ceil(MIN_CARDS / reel.length) }, (_, copy) =>
  reel.map((item) => ({ item, copy })),
).flat();

/**
 * How far a card's centre sits from the middle, for every hundredth of a card out to the row's edge:
 * the widths of everything between (the area under the size curve) plus a gap per card.
 */
const STEPS_PER_CARD = 100;
const CENTRES = (() => {
  const table = [0];
  const end = (CARDS.length / 2) * STEPS_PER_CARD;
  for (let n = 1; n <= end; n++) {
    const a = (n - 1) / STEPS_PER_CARD;
    const b = n / STEPS_PER_CARD;
    table.push(table[n - 1] + (CARD_W * (sizeAt(a) + sizeAt(b))) / 2 / STEPS_PER_CARD + GAP / STEPS_PER_CARD);
  }
  return table;
})();

function centreAt(distance: number) {
  const at = Math.min(distance * STEPS_PER_CARD, CENTRES.length - 1);
  const n = Math.floor(at);
  const next = CENTRES[Math.min(n + 1, CENTRES.length - 1)];
  return CENTRES[n] + (next - CENTRES[n]) * (at - n);
}

/** The library's case studies gliding right to left, the one passing the middle the biggest. Hover stops it. */
export function HeroReel({ className, style }: { className?: string; style?: CSSProperties }) {
  const row = useRef<HTMLDivElement>(null);
  const slots = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const count = CARDS.length;
    const still = reducedMotion();

    let position = 0;
    let speed = still ? 0 : SPEED;
    let unit = 1;
    let half = window.innerWidth / 2;
    let hovering = false;
    let focused = false;
    let last = performance.now();
    let frame = 0;
    let onScreen = true;

    const measure = () => {
      unit = Math.min(1, Math.max(0.5, window.innerWidth / 1440));
      half = window.innerWidth / 2;
      el.style.setProperty('--unit', String(unit));
      place();
    };

    function place() {
      slots.current.forEach((slot, i) => {
        if (!slot) return;
        // Distance from the middle in cards, wrapped so every card comes round again from the right.
        let k = (((i - position) % count) + count) % count;
        if (k >= count / 2) k -= count;
        const size = sizeAt(Math.abs(k));
        const x = Math.sign(k) * centreAt(Math.abs(k)) * unit;
        slot.style.transform = `translate3d(${x}px, 0, 0) scale(${size * unit})`;
        slot.style.zIndex = String(100 - Math.round(Math.abs(k) * 10));
        slot.style.visibility = Math.abs(x) - (CARD_W * size * unit) / 2 > half + 40 ? 'hidden' : 'visible';
      });
    }

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const stopped = hovering || focused;
      const target = stopped || still ? 0 : SPEED;
      // Ease the speed, never the place: a short stop under the pointer, a quick start after.
      speed += (target - speed) * (1 - Math.exp(-dt / (stopped ? STOP_IN : GO_IN)));
      if (speed < 1e-4 && target === 0) speed = 0;
      if (speed !== 0) {
        position += speed * dt;
        place();
      }
      frame = onScreen ? requestAnimationFrame(tick) : 0;
    };

    const view = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen && !frame) {
        last = performance.now();
        frame = requestAnimationFrame(tick);
      }
    });

    const enter = () => {
      hovering = true;
    };
    const leave = () => {
      hovering = false;
    };
    const focusIn = () => {
      focused = true;
    };
    const focusOut = () => {
      focused = false;
    };

    measure();
    window.addEventListener('resize', measure);
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('focusin', focusIn);
    el.addEventListener('focusout', focusOut);
    view.observe(el);
    frame = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('resize', measure);
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('pointerleave', leave);
      el.removeEventListener('focusin', focusIn);
      el.removeEventListener('focusout', focusOut);
      view.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={row} className={`${styles.reel} ${className ?? ''}`} style={style}>
      <div className={styles.track}>
        {CARDS.map(({ item, copy }, i) => {
          // Only the first copy of a case study is a real link; the repeats are for the eye.
          const real = copy === 0;
          const art = <img className={styles.art} src={item.art} alt="" draggable={false} />;
          return (
            <div
              key={`${item.title}-${copy}`}
              ref={(el) => {
                slots.current[i] = el;
              }}
              className={styles.slot}
            >
              {item.href ? (
                <a
                  className={`${styles.card} ${styles.link}`}
                  href={item.href}
                  aria-label={real ? item.title : undefined}
                  aria-hidden={real ? undefined : true}
                  tabIndex={real ? undefined : -1}
                >
                  {art}
                </a>
              ) : (
                <div className={styles.card} aria-hidden>
                  {art}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
